import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PageHeader } from "../../ds";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { PageErrorBoundary } from "../../components/PageErrorBoundary";
import { ProjectSeoTab, ProjectMarketingTab } from "../project/ProjectSeoMarketingTabs";

const LS_KEY = "office.hub.projectSlug";

function useProjectPicker() {
  const { data } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const projects = (data?.projects || []).filter((p) => p.status !== "archived");
  const [slug, setSlug] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
  });
  useEffect(() => {
    if (projects.length === 0) return;
    const stillExists = projects.some((p) => p.slug === slug);
    if (!stillExists) setSlug(projects[0].slug);
  }, [projects, slug]);
  useEffect(() => { try { if (slug) localStorage.setItem(LS_KEY, slug); } catch {} }, [slug]);
  return { projects, slug, setSlug };
}

function Picker({ projects, slug, setSlug }) {
  if (projects.length <= 1) {
    return (
      <p className="font-mono text-[11px] text-[color:var(--muted)]" data-testid="hub-picker-single">
        {projects[0]?.name || "No project"}
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--muted)]">Project</span>
      <Select value={slug} onValueChange={setSlug}>
        <SelectTrigger className="w-56" data-testid="hub-picker"><SelectValue /></SelectTrigger>
        <SelectContent>
          {projects.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Hub({ title, subtitle, body }) {
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px] mx-auto space-y-5">
      <PageHeader title={title} subtitle={subtitle} />
      {body}
    </div>
  );
}

/** SEO hub pages — four thin wrappers around ProjectSeoTab with a section. */
function SeoPage({ section, title, subtitle }) {
  const { projects, slug, setSlug } = useProjectPicker();
  const body = (
    <>
      <div data-testid="hub-picker-wrap"><Picker projects={projects} slug={slug} setSlug={setSlug} /></div>
      {slug
        ? <ProjectSeoTab slug={slug} section={section} />
        : <p className="text-sm text-[color:var(--muted)]">No projects to show.</p>}
    </>
  );
  return <PageErrorBoundary><Hub title={title} subtitle={subtitle} body={body} /></PageErrorBoundary>;
}

export function SeoOverview()  { return <SeoPage section="overview" title="SEO — Overview"  subtitle="GSC + GA4 at a glance" />; }
export function SeoReports()   { return <SeoPage section="reports"  title="SEO — Reports"   subtitle="Monthly reports" />; }
export function SeoKeywords()  { return <SeoPage section="keywords" title="SEO — Keywords"  subtitle="Tracked queries + top queries/pages" />; }
export function SeoWorklog()   { return <SeoPage section="worklog"  title="SEO — Worklog"   subtitle="Agent-written work log" />; }

/** Marketing hub pages — three thin wrappers around ProjectMarketingTab. */
function MarketingPage({ section, title, subtitle }) {
  const { projects, slug, setSlug } = useProjectPicker();
  const body = (
    <>
      <div data-testid="hub-picker-wrap"><Picker projects={projects} slug={slug} setSlug={setSlug} /></div>
      {slug
        ? <ProjectMarketingTab slug={slug} section={section} />
        : <p className="text-sm text-[color:var(--muted)]">No projects to show.</p>}
    </>
  );
  return <PageErrorBoundary><Hub title={title} subtitle={subtitle} body={body} /></PageErrorBoundary>;
}

export function MarketingQueue()   { return <MarketingPage section="queue"   title="Marketing — Publish queue" subtitle="Scheduled posts, licence HOLD visible" />; }
export function MarketingPlans()   { return <MarketingPage section="plans"   title="Marketing — Plan versions" subtitle="Draft iterations with approval marks" />; }
export function MarketingGallery() { return <MarketingPage section="gallery" title="Marketing — Visual gallery" subtitle="PNG / WEBM assets by channel" />; }
